import { isPresent } from "@apl-digital/utils";
import { createSlice } from "@reduxjs/toolkit";

import type { ITheatreLayout } from "@/api/interfaces/theatreLayouts";
import type { ResponseFailed } from "@/api/types";
import { getSessionStorage, SessionStorageKey } from "@/constants/storage";
import {
  setStateFailed,
  setStatePending,
} from "@/store/apiRequestStatusMachine";

import { getTheatreResource } from "./actions";
import type { TheatreResourceState } from "./types";

const getInitialState = (): TheatreResourceState => {
  const theatreResource = getSessionStorage(SessionStorageKey.THEATER_RESOURCE);

  return {
    theatreResource: isPresent(theatreResource)
      ? { state: theatreResource, status: "stale" }
      : { status: "idle" },
    lastRequestId: null,
  };
};

export const theatreResourceSlice = createSlice({
  name: "theatreResource",
  initialState: getInitialState(),
  reducers: {
    resetTheatreResource: (state) => {
      state.theatreResource = getInitialState().theatreResource;
      state.lastRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTheatreResource.pending, (state, { meta }) => {
        state.lastRequestId = meta.requestId;
        setStatePending<ITheatreLayout, ResponseFailed>(
          state.theatreResource,
          (nextState) => (state.theatreResource = nextState),
        );
      })
      .addCase(getTheatreResource.fulfilled, (state, { payload, meta }) => {
        if (meta.requestId !== state.lastRequestId) {
          return;
        }

        state.theatreResource = {
          state: payload,
          status: "succeeded",
        };
      })
      .addCase(getTheatreResource.rejected, (state, { payload, meta }) => {
        if (meta.requestId !== state.lastRequestId) {
          return;
        }

        setStateFailed<ITheatreLayout, ResponseFailed>(
          state.theatreResource,
          (nextState) =>
            (state.theatreResource = { ...nextState, state: payload! }),
        );
      });
  },
  selectors: {
    selectTheatreResource: (state) => state.theatreResource,
  },
});

export const { resetTheatreResource } = theatreResourceSlice.actions;

export const { selectTheatreResource } = theatreResourceSlice.selectors;

export default theatreResourceSlice.reducer;
